"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AttributeFilterButton = exports.AttributeFilterButtonCore = void 0;
var tslib_1 = require("tslib");
// (C) 2021-2022 GoodData Corporation
var react_1 = tslib_1.__importStar(require("react"));
var react_intl_1 = require("react-intl");
var sdk_model_1 = require("@gooddata/sdk-model");
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var AttributeFilterUtils_1 = require("./utils/AttributeFilterUtils");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var json_stable_stringify_1 = tslib_1.__importDefault(require("json-stable-stringify"));
var useAttributeFilterButtonState_1 = require("./AttributeFilterButton/hooks/useAttributeFilterButtonState");
var constants_1 = require("./AttributeFilterButton/constants");
var AttributeFilterButtonDefaultError_1 = tslib_1.__importDefault(require("./AttributeFilterButton/components/AttributeFilterButtonDefaultError"));
var useAttributeFilterButtonTotalCount_1 = require("./AttributeFilterButton/hooks/useAttributeFilterButtonTotalCount");
var useParentFilterTitles_1 = require("./AttributeFilterButton/hooks/useParentFilterTitles");
var useLoadMissingData_1 = require("./AttributeFilterButton/hooks/useLoadMissingData");
var AttributeFilterButtonUtils_1 = require("./AttributeFilterButton/AttributeFilterButtonUtils");
var useFetchInitialElements_1 = require("./AttributeFilterButton/hooks/useFetchInitialElements");
var useOriginalTotalElementsCount_1 = require("./AttributeFilterButton/hooks/useOriginalTotalElementsCount");
var useAttribute_1 = require("./AttributeFilterButton/hooks/useAttribute");
var useOnErrorCallback_1 = require("./AttributeFilterButton/hooks/useOnErrorCallback");
var AttributeFilterButtonDropdown_1 = tslib_1.__importDefault(require("./AttributeFilterButton/components/AttributeFilterButtonDropdown"));
exports.AttributeFilterButtonCore = function (props) {
    var _a, _b, _c, _d, _e, _f, _g;
    ts_invariant_1.default(!(props.filter && props.connectToPlaceholder), "It's not possible to combine 'filter' property with 'connectToPlaceholder' property. Either provide a value, or a placeholder.");
    ts_invariant_1.default(!(props.filter && !props.onApply), "It's not possible to use 'filter' property without 'onApply' property. Either provide 'onApply' callback or use placeholders.");
    var _h = sdk_ui_1.usePlaceholder(props.connectToPlaceholder), resolvedPlaceholder = _h[0], setPlaceholderValue = _h[1];
    var currentFilter = resolvedPlaceholder || props.filter;
    var filterRef = sdk_model_1.filterObjRef(currentFilter);
    var isElementsByRef = sdk_model_1.isAttributeElementsByRef(sdk_model_1.filterAttributeElements(currentFilter));
    var currentFilterObjRef = react_1.useMemo(function () { return filterRef; }, [json_stable_stringify_1.default(filterRef)]);
    var backendWithTelemetry = react_1.useMemo(function () { return AttributeFilterButtonUtils_1.getBackend(props.backend, props); }, [props.backend]);
    var _j = useAttributeFilterButtonState_1.useAttributeFilterButtonState(currentFilter, backendWithTelemetry), state = _j.state, onCurrentFilterChange = _j.onCurrentFilterChange, clearUriToElementMap = _j.clearUriToElementMap, resetSelection = _j.resetSelection, mapInitialSelectionElements = _j.mapInitialSelectionElements, resolveAttributeElements = _j.resolveAttributeElements, onSearch = _j.onSearch, onElementSelect = _j.onElementSelect, backupIsInverted = _j.backupIsInverted, removeFilteringStatus = _j.removeFilteringStatus, onRangeChange = _j.onRangeChange, onDropdownClosed = _j.onDropdownClosed, onDropdownOpen = _j.onDropdownOpen;
    var resolvedParentFilters = sdk_ui_1.useResolveValueWithPlaceholders(props.parentFilters);
    var prevParentFilters = sdk_ui_1.usePrevious(resolvedParentFilters);
    react_1.useEffect(function () {
        clearUriToElementMap();
    }, [props.backend, props.workspace, props.identifier, json_stable_stringify_1.default(currentFilterObjRef)]);
    react_1.useEffect(function () {
        onCurrentFilterChange(currentFilter);
    }, [currentFilter]);
    react_1.useEffect(function () {
        if (!isEmpty_1.default(props.parentFilters) && !isEqual_1.default(prevParentFilters, resolvedParentFilters)) {
            resetSelection();
        }
    }, [json_stable_stringify_1.default(resolvedParentFilters)]);
    var _k = useAttribute_1.useAttribute({
        backend: backendWithTelemetry,
        workspace: props.workspace,
        identifier: props.identifier,
        filter: currentFilter,
    }), attributeError = _k.error, attribute = _k.result, attributeStatus = _k.status;
    /**
     * This cancelable promise is used to fetch attribute filter elements for the initial selected options or
     * to fetch the elements after selection change coming from the parent component.
     * It's only called on component mounting to ensure we have attribute element titles for elements out of
     * limits in case of huge element number.
     */
    var uriToAttributeElementMapError = useFetchInitialElements_1.useFetchInitialElements({
        context: {
            backend: backendWithTelemetry,
            workspace: props.workspace,
            identifier: props.identifier,
            filter: currentFilter,
        },
        state: {
            selectedFilterOptions: state.selectedFilterOptions,
            appliedFilterOptions: state.appliedFilterOptions,
        },
        isElementsByRef: isElementsByRef,
        onFetchInitialElementsSuccess: mapInitialSelectionElements,
    }).error;
    /**
     * This cancelable promise loads missing page of data if needed and in the onSuccess callback
     * it merges the newly loaded data into the already loaded data
     */
    var _l = useLoadMissingData_1.useLoadMissingData({
        context: {
            backend: backendWithTelemetry,
            workspace: props.workspace,
            filterObjRef: currentFilterObjRef,
        },
        state: {
            validOptions: state.validOptions,
            offset: state.offset,
            limit: state.limit,
            needsReloadAfterSearch: state.needsReloadAfterSearch,
            searchString: state.searchString,
            selectedFilterOptions: state.selectedFilterOptions,
            appliedFilterOptions: state.appliedFilterOptions,
        },
        ownProps: {
            parentFilters: resolvedParentFilters,
            parentFilterOverAttribute: props.parentFilterOverAttribute,
            isElementsByRef: isElementsByRef,
        },
        onLoadMissingDataSuccess: resolveAttributeElements,
    }), elementsError = _l.error, elementsStatus = _l.status;
    var _m = useOriginalTotalElementsCount_1.useOriginalTotalElementsCount({
        context: {
            backend: backendWithTelemetry,
            workspace: props.workspace,
            identifier: props.identifier,
            filter: currentFilter,
        },
        ownProps: {
            parentFilters: resolvedParentFilters,
            parentFilterOverAttribute: props.parentFilterOverAttribute,
        },
    }), originalTotalCountError = _m.error, originalTotalCount = _m.result, originalTotalCountStatus = _m.status;
    react_1.useEffect(function () {
        if (!AttributeFilterButtonUtils_1.isCancelablePromisePendingOrLoading(originalTotalCountStatus)) {
            removeFilteringStatus();
        }
    }, [originalTotalCountStatus]);
    var _o = useAttributeFilterButtonTotalCount_1.useAttributeFilterButtonTotalCount({
        context: {
            backend: backendWithTelemetry,
            workspace: props.workspace,
            filter: currentFilter,
            identifier: props.identifier,
        },
        state: {
            searchString: state.searchString,
        },
        ownProps: {
            parentFilters: resolvedParentFilters,
            parentFilterOverAttribute: props.parentFilterOverAttribute,
        },
    }), totalCountError = _o.error, totalCount = _o.result, totalCountStatus = _o.status;
    var _p = useParentFilterTitles_1.useParentFilterTitles({
        context: {
            backend: backendWithTelemetry,
            workspace: props.workspace,
        },
        ownProps: { parentFilters: resolvedParentFilters },
    }), parentFilterTitlesError = _p.error, parentFilterTitles = _p.result, parentFilterTitlesStatus = _p.status;
    useOnErrorCallback_1.useOnErrorCallback(props.onError, [
        attributeError,
        elementsError,
        totalCountError,
        parentFilterTitlesError,
        originalTotalCountError,
        uriToAttributeElementMapError,
    ]);
    var isAllFiltered = AttributeFilterUtils_1.showAllFilteredMessage(AttributeFilterButtonUtils_1.isCancelablePromiseLoading(elementsStatus), resolvedParentFilters, originalTotalCount);
    var onApply = function (closeDropdown) {
        var _a;
        backupIsInverted();
        var filter = AttributeFilterButtonUtils_1.createFilter(currentFilter, state.isInverted, state.selectedFilterOptions, props.identifier);
        if (props.connectToPlaceholder) {
            setPlaceholderValue(filter);
        }
        (_a = props.onApply) === null || _a === void 0 ? void 0 : _a.call(props, filter, state.isInverted);
        return closeDropdown();
    };
    var onDropdownOpenStateChanged = function (isOpen) {
        isOpen ? onDropdownOpen() : onDropdownClosed();
    };
    var hasNoData = !AttributeFilterButtonUtils_1.isCancelablePromisePendingOrLoading(parentFilterTitlesStatus) &&
        !(parentFilterTitles === null || parentFilterTitles === void 0 ? void 0 : parentFilterTitles.length) &&
        !AttributeFilterButtonUtils_1.isCancelablePromiseLoading(elementsStatus) &&
        originalTotalCount === 0;
    var getDropdownBodyProps = function (onApplyButtonClicked, onCloseButtonClicked, isMobile) {
        var _a, _b, _c;
        return ({
            items: (_b = (_a = state.validOptions) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : [],
            totalCount: totalCount !== null && totalCount !== void 0 ? totalCount : constants_1.ATTRIBUTE_FILTER_BUTTON_LIMIT,
            onSelect: onElementSelect,
            onRangeChange: onRangeChange,
            onSearch: onSearch,
            selectedItems: state.selectedFilterOptions,
            isInverted: state.isInverted,
            isLoading: (!((_c = state.validOptions) === null || _c === void 0 ? void 0 : _c.items) && AttributeFilterButtonUtils_1.isCancelablePromiseLoading(elementsStatus)) ||
                [totalCountStatus, originalTotalCountStatus, parentFilterTitlesStatus].some(AttributeFilterButtonUtils_1.isCancelablePromisePendingOrLoading),
            searchString: state.searchString,
            applyDisabled: AttributeFilterButtonUtils_1.getNumberOfSelectedItems(originalTotalCount, state.selectedFilterOptions, state.isInverted) === 0,
            showItemsFilteredMessage: AttributeFilterUtils_1.showItemsFilteredMessage(AttributeFilterButtonUtils_1.isCancelablePromiseLoading(elementsStatus), resolvedParentFilters) &&
                !isAllFiltered,
            parentFilterTitles: parentFilterTitles,
            onApplyButtonClicked: onApplyButtonClicked,
            onCloseButtonClicked: onCloseButtonClicked,
            isFullWidth: isMobile,
        });
    };
    var FilterError = props.FilterError;
    return elementsError ||
        attributeError ||
        totalCountError ||
        parentFilterTitlesError ||
        originalTotalCountError ||
        uriToAttributeElementMapError ? (react_1.default.createElement(FilterError, { error: (_f = (_e = (_d = (_c = (_b = (_a = elementsError === null || elementsError === void 0 ? void 0 : elementsError.message) !== null && _a !== void 0 ? _a : attributeError === null || attributeError === void 0 ? void 0 : attributeError.message) !== null && _b !== void 0 ? _b : totalCountError === null || totalCountError === void 0 ? void 0 : totalCountError.message) !== null && _c !== void 0 ? _c : parentFilterTitlesError === null || parentFilterTitlesError === void 0 ? void 0 : parentFilterTitlesError.message) !== null && _d !== void 0 ? _d : originalTotalCountError === null || originalTotalCountError === void 0 ? void 0 : originalTotalCountError.message) !== null && _e !== void 0 ? _e : uriToAttributeElementMapError === null || uriToAttributeElementMapError === void 0 ? void 0 : uriToAttributeElementMapError.message) !== null && _f !== void 0 ? _f : "Unknown error" })) : (react_1.default.createElement(AttributeFilterButtonDropdown_1.default, { isFiltering: state.isFiltering, isDropdownOpen: state.isDropdownOpen, isElementsLoading: !((_g = state.validOptions) === null || _g === void 0 ? void 0 : _g.items) && AttributeFilterButtonUtils_1.isCancelablePromiseLoading(elementsStatus), isOriginalTotalCountLoading: AttributeFilterButtonUtils_1.isCancelablePromisePendingOrLoading(originalTotalCountStatus), title: props.title || !AttributeFilterButtonUtils_1.isCancelablePromisePendingOrLoading(attributeStatus)
            ? attribute.title
            : AttributeFilterUtils_1.getLoadingTitleIntl(props.intl), subtitle: AttributeFilterButtonUtils_1.getSubtitle({
            loadingProps: {
                isElementsLoading: AttributeFilterButtonUtils_1.isCancelablePromiseLoading(elementsStatus),
                isTotalCountLoading: AttributeFilterButtonUtils_1.isCancelablePromisePendingOrLoading(totalCountStatus),
            },
            ownProps: {
                isAllFiltered: isAllFiltered,
                isElementsByRef: isElementsByRef,
                currentFilter: currentFilter,
                identifier: props.identifier,
                originalTotalCount: originalTotalCount,
                intl: props.intl,
            },
            state: {
                firstLoad: state.firstLoad,
                isFiltering: state.isFiltering,
                isInverted: state.isInverted,
                selectedFilterOptions: state.selectedFilterOptions,
                uriToAttributeElementMap: state.uriToAttributeElementMap,
                searchString: state.searchString,
            },
        }), selectedFilterOptions: state.selectedFilterOptions, onDropdownOpenStateChanged: onDropdownOpenStateChanged, onApplyButtonClicked: onApply, isAllFiltered: isAllFiltered, hasNoData: hasNoData, getDropdownBodyProps: getDropdownBodyProps, renderBody: props.renderBody }));
};
exports.AttributeFilterButtonCore.defaultProps = {
    FilterError: AttributeFilterButtonDefaultError_1.default,
};
var IntlAttributeFilterButton = sdk_ui_1.withContexts(react_intl_1.injectIntl(exports.AttributeFilterButtonCore));
/**
 * @public
 */
exports.AttributeFilterButton = function (props) {
    return (react_1.default.createElement(sdk_ui_1.IntlWrapper, { locale: props.locale },
        react_1.default.createElement(IntlAttributeFilterButton, tslib_1.__assign({}, props))));
};
//# sourceMappingURL=AttributeFilterButton.js.map